import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUrlState } from "@/lib/useUrlState";
import { useLocation } from "react-router-dom";
import SearchBar from "@/components/SearchBar";
import Segmented from "@/components/Segmented";
import {
  Loader2, CalendarDays, LayoutList, Plus, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown,
  Clock, ArrowUp, ArrowDown, X, Upload, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { preconApi } from "./lib/api";
import { openBid } from "./lib/navigation";
import BidWorkspacePage from "./BidWorkspacePage";
import { BidJobDrawer } from "./components/BidJobDrawer";
import { BoardStatusChip, BOARD_STATUSES, countdown } from "./components/BidStatusChips";
import BodToggle from "./components/BodToggle";
import BidCalendar from "./components/BidCalendar";
import HeaderFilterMenu from "./components/HeaderFilterMenu";
import ReconcileDialog from "./components/ReconcileDialog";
import FrsUploadDialog from "./components/FrsUploadDialog";
import PartyField from "./components/PartyField";
import { CustomersCell, DueDateCell, PersonCell, RowNotesButton, RowEmailsButton } from "./components/BoardCells";
import MonthPicker from "@/components/MonthPicker";

/** BID MASTER §10 — Bid Board: one Status lifecycle, header sort + filters, saved views, archived section, progress dots. */
const COLUMNS = [
  { key: "name", label: "Name", sort: (j) => j.project_name || "" },
  { key: "customers", label: "Customer(s)", filter: true, sort: (j) => (j.invitations?.[0]?.gc_name || "") },
  { key: "bid_due", label: "Bid Due", sort: (j) => j.bid_due_internal || "9999" },
  { key: "estimator", label: "Est", w: 64, filter: true, sort: (j) => j.estimator_name || j.estimator_email || "zz" },
  { key: "am", label: "AM", filter: true, sort: (j) => j.account_manager_name || j.account_manager_email || "zz" },
  { key: "status", label: "Status", filter: true, sort: (j) => BOARD_STATUSES.indexOf(j.board_status) },
  { key: "total", label: "Total", w: 96, right: true, sort: (j) => -(j.est_total_sales || 0) },
  { key: "gm", label: "GM%", w: 64, right: true, sort: (j) => -(j.est_gm_percent ?? -1) },
  { key: "start", label: "Start", w: 84, center: true, sort: (j) => j.start_date || "9999" },
  { key: "completion", label: "Completion", w: 96, center: true, sort: (j) => j.completion_date || "9999" },
  { key: "notes", label: "Notes", w: 56 },
  { key: "emails", label: "Emails", w: 60 },
  { key: "bod", label: "BOD", sort: (j) => -(j.bod_count || 0) },
];
const SEP = "|";
const ARCHIVE_STATUSES = ["Won", "Lost", "Cancelled", "No Bid"];
const MAIN_STATUSES = BOARD_STATUSES.filter((s) => !["No Bid", "Lost", "Cancelled"].includes(s));
const EMPTY_COL_FILTERS = { customers: "", estimator: "", am: "", status: "" };
const STATE_KEY = "abs.bidboard.state";
const colValues = (j, key) => {
  if (key === "customers") { const n = (j.invitations || []).map((i) => i.gc_name).filter(Boolean); return n.length ? n : ["—"]; }
  if (key === "estimator") return [j.estimator_name || j.estimator_email || "Unassigned"];
  if (key === "am") return [j.account_manager_name || j.account_manager_email || "Unassigned"];
  if (key === "type") return [j.bid_type === "B" ? "B" : "E"];
  return [j.board_status];
};
const passesColFilters = (j, f) => Object.entries(f).every(([k, v]) => {
  if (!v) return true;
  const picks = v.split(SEP);
  return colValues(j, k).some((x) => picks.includes(x));
});
const matches = (j, q) => {
  if (!q) return true;
  const s = q.toLowerCase();
  return [j.project_name, j.city, j.state, j.street, j.estimator_name, j.estimator_email,
    j.account_manager_name, (j.invitations || []).map((i) => i.gc_name).join(" ")]
    .join(" ").toLowerCase().includes(s);
};
const today = () => new Date(new Date().toDateString());
const sortRows = (rows, st) => {
  const col = COLUMNS.find((c) => c.key === st.key) || COLUMNS[2];
  return [...rows].sort((a, b) => { const x = col.sort(a), y = col.sort(b); return (x > y ? 1 : x < y ? -1 : 0) * st.dir; });
};
const monthYear = (v) => (v ? new Date(`${v.slice(0, 10)}T00:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "—");
const daysOut = (v) => (v ? Math.round((new Date(`${v}T00:00:00`) - today()) / 86400000) : null);
const dueTone = (v) => { const d = daysOut(v); return d === null ? "text-muted-foreground" : d <= 2 ? "text-red-600 font-semibold" : d <= 5 ? "text-amber-600 font-medium" : "text-foreground"; };
const fmt = (v) => (v ? v.slice(0, 10) : "—");
const readState = () => { try { return JSON.parse(sessionStorage.getItem(STATE_KEY) || "{}"); } catch { return {}; } };

export default function BidBoardPage() {
  const location = useLocation();
  const m = /^\/bids\/(estimate\/)?([^/?]+)/.exec(location.pathname);
  const wsEstimate = m && m[1] ? m[2] : null;
  const wsJob = m && !m[1] ? m[2] : null;
  if (wsJob || wsEstimate) {
    return <BidWorkspacePage key={wsJob || wsEstimate} jobId={wsJob} estimateId={wsEstimate} />;
  }
  return <Board />;
}

function Board() {
  const { access, user } = useAuth();
  const canEdit = (access?.estimating || "none") === "write";
  const saved = useRef(readState()).current;
  const [search, setSearch] = useState(saved.search || "");
  const [mode, setMode] = useUrlState("mode", "board");
  const [data, setData] = useState(null);
  // The open bid drawer is a URL entry (?open=<id>) so Back closes it and Forward reopens it.
  const [openParam, setOpenParam] = useUrlState("open", "");
  const [open, setOpenJob] = useState(null);
  const setOpen = (job) => { if (job) setOpenJob(job); setOpenParam(job ? job.id : ""); };
  useEffect(() => {
    if (!openParam) { setOpenJob(null); return; }
    if (open?.id === openParam) return;
    preconApi.getJob(openParam).then(setOpenJob).catch(() => setOpenParam(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openParam]);
  const [drawerTab, setDrawerTab] = useState("details");
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState({ project_name: "", gc_name: "", bid_due_date: "" });
  const [people, setPeople] = useState({ estimators: [], account_managers: [] });
  const [groupBy, setGroupBy] = useState(saved.groupBy || "me");
  const [sort, setSort] = useState(saved.sort || { key: "bid_due", dir: 1 });
  const [collapsed, setCollapsed] = useState(saved.collapsed || {});
  const [colFilters, setColFilters] = useState(() => { const { type, ...rest } = saved.colFilters || {}; return { ...EMPTY_COL_FILTERS, ...rest }; });
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveSearch, setArchiveSearch] = useState("");
  const [archiveStatusFilter, setArchiveStatusFilter] = useState(ARCHIVE_STATUSES);
  const [archiveSort, setArchiveSort] = useState({ key: "bid_due", dir: 1 });
  const [reconcile, setReconcile] = useState(null);
  const [prefs, setPrefs] = useState({ widths: {}, all_collapsed: false });
  const [frsOpen, setFrsOpen] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    preconApi.assignable().then((r) => setPeople({ estimators: r.estimators || [], account_managers: r.account_managers || [] })).catch(() => {});
    preconApi.getColumns().then((p) => { setPrefs(p); if (p.all_collapsed && !saved.collapsed) setCollapsed({ __all: true }); }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Board state survives a trip into the workspace and back (filters, sort, scroll).
  useEffect(() => { sessionStorage.setItem(STATE_KEY, JSON.stringify({ search, groupBy, sort, collapsed, colFilters, scrollTop: saved.scrollTop })); }, [search, groupBy, sort, collapsed, colFilters]); // eslint-disable-line react-hooks/exhaustive-deps
  const onScroll = (e) => { saved.scrollTop = e.currentTarget.scrollTop; sessionStorage.setItem(STATE_KEY, JSON.stringify({ ...readState(), scrollTop: saved.scrollTop })); };

  const load = useCallback(() => preconApi.listJobs({ view: "all", include_archived: true })
    .then((d) => { setData(d); requestAnimationFrame(() => { if (scrollRef.current && saved.scrollTop) scrollRef.current.scrollTop = saved.scrollTop; }); })
    .catch(() => toast.error("Could not load the bid board")), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const assignPerson = async (job, which, email) => {
    const list = which === "estimator" ? people.estimators : people.account_managers;
    const person = list.find((p) => p.email === email);
    try {
      await preconApi.assign(job.id, which === "estimator" ? { estimator_email: email, estimator_name: person?.name || "" }
        : { account_manager_email: email, account_manager_name: person?.name || "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "That assignment did not save"); }
  };
  const patchJob = async (job, body) => {
    try { await preconApi.patchJob(job.id, body); load(); } catch (e) { toast.error(e?.response?.data?.detail || "That change did not save"); }
  };
  const openJob = async (id, tab = "details") => { try { setDrawerTab(tab); setOpen(await preconApi.getJob(id)); } catch { toast.error("Could not open that job"); } };
  const refreshOpen = async () => { if (open) setOpen(await preconApi.getJob(open.id)); load(); };
  const toggleArchiveStatus = (k) => setArchiveStatusFilter((c) => c.includes(k) ? c.filter((x) => x !== k) : [...c, k]);
  const savePrefs = (patch) => { const nxt = { ...prefs, ...patch }; setPrefs(nxt); preconApi.putColumns(patch).catch(() => {}); };


  const liveRows = useMemo(() => (data ? data.items.filter((j) => j.stage !== "Archived" && (MAIN_STATUSES.includes(j.board_status) || j.in_am_review)) : []), [data]);
  const me = (user?.email || "").toLowerCase();
  const isMine = (j) => !!me && [j.estimator_email, j.account_manager_email].some((e) => e && e.toLowerCase() === me);
  const myCount = useMemo(() => liveRows.filter(isMine).length, [liveRows, me]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (data && groupBy === "me" && myCount === 0) setGroupBy("status"); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const sorted = useMemo(() => sortRows(liveRows.filter((j) => (groupBy !== "me" || isMine(j)) && passesColFilters(j, colFilters) && matches(j, search)), sort), [liveRows, sort, colFilters, search, groupBy, me]); // eslint-disable-line react-hooks/exhaustive-deps
  const activeBids = useMemo(() => {
    const acc = {};
    liveRows.forEach((j) => [j.estimator_email, j.account_manager_email].forEach((e) => { if (e) acc[e.toLowerCase()] = (acc[e.toLowerCase()] || 0) + 1; }));
    return acc;
  }, [liveRows]);
  const colOptions = (key) => {
    const counts = new Map();
    liveRows.forEach((j) => colValues(j, key).forEach((v) => counts.set(v, (counts.get(v) || 0) + 1)));
    const keys = key === "status" ? [...MAIN_STATUSES, "No Bid"] : key === "type" ? ["E", "B"] : [...counts.keys()].sort((a, b) => a.localeCompare(b));
    return keys.map((v) => ({ value: v, label: v === "No Bid" ? "No Bid · AM Review" : v, count: counts.get(v) || 0 }));
  };
  const archiveRows = useMemo(() => {
    if (!data) return [];
    return sortRows(data.items.filter((j) => j.stage === "Archived" && archiveStatusFilter.includes(j.board_status) && matches(j, archiveSearch)), archiveSort);
  }, [data, archiveStatusFilter, archiveSearch, archiveSort]);
  const archiveTotal = useMemo(() => (data ? data.items.filter((j) => j.stage === "Archived").length : 0), [data]);

  const groups = useMemo(() => {
    const out = [];
    if (groupBy === "estimator" || groupBy === "am") {
      const g = new Map();
      sorted.forEach((j) => {
        const k = groupBy === "estimator" ? (j.estimator_name || j.estimator_email || "Unassigned") : (j.account_manager_name || j.account_manager_email || "Unassigned");
        (g.get(k) || g.set(k, []).get(k)).push(j);
      });
      [...g.keys()].sort().forEach((k) => out.push({ key: k, rows: g.get(k) }));
    } else {
      const order = [...MAIN_STATUSES, "No Bid"];
      (sort.key === "status" && sort.dir < 0 ? [...order].reverse() : order).forEach((s) => {
        const rows = sorted.filter((j) => j.board_status === s);
        if (rows.length) out.push({ key: s === "No Bid" ? "AM Review" : s, rows });
      });
    }
    return out;
  }, [sorted, groupBy, sort]);
  const isCollapsed = (k) => (collapsed.__all ? collapsed[k] !== false : !!collapsed[k]);
  const setAll = (on) => { setCollapsed(on ? { __all: true } : {}); savePrefs({ all_collapsed: on }); };

  // Column widths — drag the header edge; remembered per user.
  const resize = (key, e) => {
    const th = e.currentTarget.parentElement; const x0 = e.clientX; const w0 = th.getBoundingClientRect().width;
    const move = (ev) => setPrefs((p) => ({ ...p, widths: { ...p.widths, [key]: Math.max(56, Math.round(w0 + ev.clientX - x0)) } }));
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); setPrefs((p) => { preconApi.putColumns({ widths: p.widths }).catch(() => {}); return p; }); };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up); e.preventDefault();
  };
  const header = (c, st, setSt, filt = true) => (
    <th key={c.key} style={prefs.widths?.[c.key] ? { width: prefs.widths[c.key], minWidth: prefs.widths[c.key] } : c.w ? { width: c.w } : undefined}
        className={`relative px-3 py-2 ${c.center ? "text-center" : c.right ? "text-right" : "text-left"} font-semibold select-none ${c.filter && filt ? "" : c.sort ? "cursor-pointer" : ""}`}
        onClick={() => !(c.filter && filt) && c.sort && setSt((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : 1 }))}
        data-testid={`board-col-${c.key}`} data-active={st.key === c.key ? "1" : "0"}>
      {c.filter && filt ? (
        <HeaderFilterMenu label={c.label} testid={`board-${c.key}`} value={colFilters[c.key]} multi separator={SEP}
                          options={colOptions(c.key)} onChange={(v) => setColFilters((f) => ({ ...f, [c.key]: v }))}
                          onSort={(dir) => setSt({ key: c.key, dir: dir === "asc" ? 1 : -1 })}
                          sortActive={st.key === c.key} direction={st.dir > 0 ? "asc" : "desc"} sortOnLabel />
      ) : (
        <span className="inline-flex items-center gap-1">
          {c.label}
          {st.key === c.key && (st.dir > 0 ? <ArrowUp className="h-3 w-3" data-testid={`board-col-${c.key}-asc`} /> : <ArrowDown className="h-3 w-3" data-testid={`board-col-${c.key}-desc`} />)}
        </span>
      )}
      <span onMouseDown={(e) => resize(c.key, e)} onClick={(e) => e.stopPropagation()} data-testid={`board-col-resize-${c.key}`}
            className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-[#007BFF]/40" />
    </th>
  );
  const activeColFilters = Object.values(colFilters).filter(Boolean).length;
  const myAm = (j) => user?.email && j.account_manager_email && j.account_manager_email.toLowerCase() === user.email.toLowerCase();

  if (!data) return <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading the bid board…</div>;

  const cell = (j, key) => {
    switch (key) {
      case "name": return (
        <td key={key} className="max-w-[20rem] px-3 py-2 font-medium text-foreground">
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={(e) => { e.stopPropagation(); openBid(j.id); }} className="truncate text-left hover:text-[#007BFF] hover:underline" data-testid={`board-name-${j.id}`}>{j.project_name}</button></div>
          <div className="truncate text-[10px] font-normal text-muted-foreground">{[j.city, j.state].filter(Boolean).join(", ") || "—"}</div>
        </td>);
      case "customers": return (
        <td key={key} className="px-3 py-2" onClick={(e) => e.stopPropagation()}><CustomersCell job={j} canEdit={canEdit} onChanged={load} /></td>);
      case "bid_due": return (
        <td key={key} className={`whitespace-nowrap px-3 py-2 ${dueTone(j.bid_due_internal)} ${canEdit && !j.bid_due_internal ? "text-center" : ""}`} data-testid={`board-due-${j.id}`}
            title={j.bid_due_at ? `GC due ${fmt(j.bid_due_at)} · internal = GC due − 2 days unless changed here` : "No due date"}>
          {canEdit ? <DueDateCell value={j.bid_due_internal} testid={`board-due-input-${j.id}`} className={dueTone(j.bid_due_internal)} onSave={(v) => patchJob(j, { bid_due_internal_override: v })} /> : fmt(j.bid_due_internal)}</td>);
      case "estimator": case "am": {
        const isEst = key === "estimator";
        const email = isEst ? j.estimator_email : j.account_manager_email;
        return (
          <td key={key} className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
            <PersonCell job={j} which={key} canEdit={canEdit} people={isEst ? people.estimators : people.account_managers}
                        activeCount={email ? activeBids[email.toLowerCase()] || 0 : 0}
                        onAssign={(v) => assignPerson(j, isEst ? "estimator" : "am", v)} />
          </td>);
      }
      case "status": return (
        <td key={key} className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1">
            <BoardStatusChip job={j} canEdit={canEdit} onChanged={load} allJobs={data.items} />
            {j.in_am_review && <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-700" data-testid={`am-review-countdown-${j.id}`} title={`AM review until ${new Date(j.am_review_until).toLocaleString()}`}><Clock className="h-3 w-3" />{countdown(j.am_review_until)}</span>}
            {j.board_status === "Reconcile" && canEdit && (
              <button type="button" onClick={(e) => { e.stopPropagation(); setReconcile(j); }} title="Reconcile complete"
                      className="rounded border border-emerald-300 bg-emerald-50 p-0.5 text-emerald-700 hover:bg-emerald-100" data-testid={`reconcile-done-${j.id}`}><Check className="h-3 w-3" /></button>)}
            {j.stage === "Archived" && j.outcome && <span className="text-[10px] text-muted-foreground" data-testid={`board-outcome-${j.id}`}>· {j.outcome}{j.no_bid?.reason ? ` (${j.no_bid.reason})` : ""}</span>}
          </div>
        </td>);
      case "start": case "completion": {
        const f = key === "start" ? "start_date" : "completion_date";
        const txt = <span className="whitespace-nowrap text-[11px]">{monthYear(j[f]) || "—"}</span>;
        const blank = canEdit && !j[f];
        return <td key={key} className="whitespace-nowrap px-3 py-2 text-center text-muted-foreground" data-testid={`board-${key}-${j.id}`} onClick={(e) => e.stopPropagation()}>
          {canEdit ? <MonthPicker value={j[f]} iconOnly={blank} testid={`board-${key}-input-${j.id}`} onChange={(v) => patchJob(j, { [f]: v })}>{txt}</MonthPicker> : txt}</td>;
      }
      case "notes": return <td key={key} className="px-3 py-2" onClick={(e) => e.stopPropagation()}><RowNotesButton job={j} onChanged={load} /></td>;
      case "emails": return <td key={key} className="px-3 py-2" onClick={(e) => e.stopPropagation()}><RowEmailsButton job={j} /></td>;
      case "total": return <td key={key} className="px-3 py-2 text-right tabular-nums whitespace-nowrap" data-testid={`board-total-${j.id}`}>{j.est_total_sales ? `$${Math.round(j.est_total_sales).toLocaleString()}` : "—"}</td>;
      case "gm": return <td key={key} className="px-3 py-2 text-right tabular-nums whitespace-nowrap" data-testid={`board-gm-${j.id}`}>{j.est_total_sales ? `${(j.est_gm_percent * 100).toFixed(1)}%` : "—"}</td>;
      case "bod": return <td key={key} className="px-3 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}><BodToggle job={j} canEdit={canEdit} onChanged={load} /></td>;
      default: return null;
    }
  };

  return (
    <div className="page-body" data-testid="bid-board-page" ref={scrollRef} onScroll={onScroll}>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <SearchBar value={search} onChange={setSearch} placeholder="Search..." testId="board-search" />
          <Button size="icon" variant="outline" className="h-7 w-7 border-slate-300 text-slate-800 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-800" title={mode === "board" ? "Calendar view" : "Board view"}
                  onClick={() => setMode(mode === "board" ? "calendar" : "board")} data-testid="board-mode-toggle">
            {mode === "board" ? <CalendarDays className="h-4 w-4" /> : <LayoutList className="h-4 w-4" />}
          </Button>
          <Segmented value={groupBy} onChange={setGroupBy}
                     options={[{ key: "me", label: `My Bids${myCount ? ` (${myCount})` : ""}`, testId: "board-group-me" },
                               { key: "status", label: "Status", testId: "board-group-status" },
                               { key: "estimator", label: "Estimator", testId: "board-group-estimator" },
                               { key: "am", label: "AM", testId: "board-group-am" }]} />
          {canEdit && (
            <Button size="sm" className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" title="New Bid" onClick={() => setAddOpen(true)} data-testid="board-add-btn">
              <Plus className="mr-0.5 h-3.5 w-3.5" /> Bid
            </Button>)}
          {activeColFilters > 0 && (
            <button type="button" onClick={() => setColFilters(EMPTY_COL_FILTERS)} data-testid="board-col-filters-clear"
                    className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground hover:text-red-600"><X className="h-3 w-3" /> Clear {activeColFilters}</button>)}
        </div>

        {mode === "board" ? (<>
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-xs" data-testid="board-table">
              <thead className="bg-muted/40 text-[11px] text-muted-foreground"><tr>
                <th className="w-6 px-1 py-2">
                  <button type="button" title={collapsed.__all ? "Expand all" : "Collapse all"} onClick={() => setAll(!collapsed.__all)} data-testid="board-collapse-all"
                          className="text-muted-foreground hover:text-foreground">{collapsed.__all ? <ChevronsUpDown className="h-3.5 w-3.5" /> : <ChevronsDownUp className="h-3.5 w-3.5" />}</button>
                </th>
                {COLUMNS.map((c) => header(c, sort, setSort))}</tr></thead>
              <tbody>
                {!sorted.length && <tr><td colSpan={COLUMNS.length + 1} className="py-12 text-center text-muted-foreground" data-testid="board-empty">
                  {groupBy === "me" ? "No live bids are assigned to you as EST or AM. Switch to By Status to see the whole board." : "Nothing matches. Push an ITB from the inbox to Qualify and it lands here."}</td></tr>}
                {groups.map((g) => (
                  <GroupRows key={g.key} group={g} cols={COLUMNS.length + 1} collapsed={isCollapsed(g.key)} onToggle={() => setCollapsed((c) => ({ ...c, [g.key]: c.__all ? c[g.key] === false : !c[g.key] }))}>
                    {g.rows.map((j) => (
                      <tr key={j.id} onClick={() => openJob(j.id)} data-testid={`board-row-${j.id}`} data-status={j.board_status}
                          className={`cursor-pointer border-t border-border hover:bg-muted/40 ${myAm(j) && j.in_am_review ? "bg-amber-50/60 dark:bg-amber-950/20" : ""}`}>
                        <td />{COLUMNS.map((c) => cell(j, c.key))}
                      </tr>))}
                  </GroupRows>))}
              </tbody>
            </table>
          </div>

          <div className="rounded-lg border border-border bg-card" data-testid="board-archive-section">
            <button type="button" onClick={() => setArchiveOpen((o) => !o)} data-testid="board-archive-toggle"
                    className="flex w-full items-center gap-1.5 px-3 py-2 text-[11px] font-semibold text-foreground">
              {archiveOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              Archived <span className="font-normal text-muted-foreground">· {archiveTotal} — Won / Lost / Cancelled / No Bid, never deleted</span>
            </button>
            {archiveOpen && (
              <div className="border-t border-border p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <SearchBar value={archiveSearch} onChange={setArchiveSearch} placeholder="Search..." testId="board-archive-search" />
                  <div className="flex items-center gap-3 text-[11px]">
                    {ARCHIVE_STATUSES.map((s) => (
                      <label key={s} className="flex items-center gap-1"><Checkbox checked={archiveStatusFilter.includes(s)} onCheckedChange={() => toggleArchiveStatus(s)} data-testid={`board-archive-filter-${s.replace(" ", "-")}`} />{s}</label>))}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="board-archive-table">
                    <thead className="bg-muted/40 text-[11px] text-muted-foreground"><tr><th className="w-6" />{COLUMNS.map((c) => header(c, archiveSort, setArchiveSort, false))}</tr></thead>
                    <tbody>
                      {!archiveRows.length && <tr><td colSpan={COLUMNS.length + 1} className="py-6 text-center text-muted-foreground" data-testid="board-archive-empty">Nothing archived matches.</td></tr>}
                      {archiveRows.map((j) => (
                        <tr key={j.id} onClick={() => openJob(j.id)} data-testid={`board-archive-row-${j.id}`} data-status={j.board_status} className="cursor-pointer border-t border-border opacity-80 hover:bg-muted/40">
                          <td />{COLUMNS.map((c) => cell(j, c.key))}
                        </tr>))}
                    </tbody>
                  </table>
                </div>
              </div>)}
          </div>
        </>) : (
          <BidCalendar people={people} onOpen={(id) => openBid(id)} />
        )}
      </div>

      {open && <BidJobDrawer key={`${open.id}-${drawerTab}`} job={open} initialTab={drawerTab} onClose={() => { setOpen(null); load(); }} onChanged={refreshOpen} />}
      {reconcile && <ReconcileDialog job={reconcile} onClose={() => setReconcile(null)} onDone={load} />}
      <FrsUploadDialog open={frsOpen} onOpenChange={setFrsOpen}
                       onCreated={async (estimateId) => {
                         try { const job = await preconApi.jobFromEstimate(estimateId); load(); openBid(job.id, "estimate"); }
                         catch (e) { toast.error(e?.response?.data?.detail || "Estimate created, but the bid record failed"); }
                       }} />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="board-add-dialog">
          <DialogHeader><DialogTitle>New Bid</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {canEdit && (
              <button type="button" onClick={() => { setAddOpen(false); setFrsOpen(true); }} data-testid="board-add-upload-takeoff"
                      className="flex w-full items-center gap-2 rounded-md border border-dashed border-[#007BFF]/50 bg-[#007BFF]/5 px-3 py-2 text-left text-xs text-foreground hover:bg-[#007BFF]/10">
                <Upload className="h-4 w-4 text-[#007BFF]" />
                <span><b>Upload takeoff (FRS)</b> — creates the bid from the workbook, sets New Bid and builds the estimate</span>
              </button>)}
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">or enter by hand</div>
            <Input placeholder="Project name" value={draft.project_name} onChange={(e) => setDraft({ ...draft, project_name: e.target.value })} className="h-8 text-xs" data-testid="board-add-project" />
            <PartyField value={draft.gc_name} type="gc" placeholder="General contractor (Directory)" testId="board-add-gc"
                        onChange={(name, company) => setDraft({ ...draft, gc_name: name, gc_company_id: company?.id || null })} />
            <Input type="date" value={draft.bid_due_date} onChange={(e) => setDraft({ ...draft, bid_due_date: e.target.value })} className="h-8 text-xs dark:[color-scheme:dark]" data-testid="board-add-due" />
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="board-add-cancel" onClick={() => { setAddOpen(false); setDraft({ project_name: "", gc_name: "", bid_due_date: "" }); }}>Cancel</Button>
            <Button data-testid="board-add-submit" disabled={!draft.project_name.trim()}
                    onClick={() => preconApi.createJob(draft).then((job) => { setAddOpen(false); setDraft({ project_name: "", gc_name: "", bid_due_date: "" }); load(); setOpen(job); })
                      .catch((e) => toast.error(e?.response?.data?.detail || "Could not add it"))}>Add it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function GroupRows({ group, cols, collapsed, onToggle, children }) {
  return (
    <>
      <tr className="border-t border-border bg-muted/60" data-testid={`board-group-${group.key}`}>
        <td colSpan={cols} className="px-3 py-1.5">
          <button type="button" onClick={onToggle} className="flex items-center gap-1.5 text-[11px] font-semibold text-foreground" data-testid={`board-group-toggle-${group.key}`}>
            {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {group.key}<span className="font-normal text-muted-foreground">· {group.rows.length}</span>
          </button>
        </td>
      </tr>
      {!collapsed && children}
    </>
  );
}
